import React, { useEffect, useMemo, useRef } from 'react';
import { Spin } from 'antd';
import { FormProps } from 'antd/lib/form';
import { useDebouncedCallback } from 'use-debounce';
import produce from 'immer';
import { cloneDeep, isEmpty, isEqual, merge, mergeWith } from 'lodash';
import { useLocation } from 'react-router';

import { ApplicationPayload } from '~/types/ApplicationPayload';
import { useGetApplication, useSubmitStep } from '~/controllers/wizard';
import { useMutableCallback } from '~/hooks';
import { useCommonErrors, useValidator } from '~/controllers/validation';
import { useDraftIsStale, useGetDraft, useSaveDraft } from '~/controllers/draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { getRoles, isAgent } from '~/utils/getDecodedJwt';
import { isReadOnly } from '~/utils/isReadOnly';
import { isSomeFieldsDisabled } from '~/utils/isSomeFieldsDisabled';
import { getStagePayload } from '~/utils/getStagePayload';
import { Stage } from '~/types/Application';
import { usePostAgentUpdatedFields } from '~/controllers/agentReview';
import { buildUpdatedFieldsDiff } from '~/utils/buildUpdatedFieldsDiff';
import { TRACKED_STEP_KEYS } from '~/constants/agentReview';
import Form from '../Form';
import { FormItemContextProvider } from '../Form/FormItemContext';

const SAVE_DRAFT_DEBOUNCE_MS = 1000;

interface WizardFormProps<Values> extends Omit<FormProps<Values>, 'initialValue'> {
  stage?: Stage;
  payloadKey: keyof ApplicationPayload;
  validationFnName?: string;
  disabled?: boolean;
  onBeforeFinish?: (values: Values) => Promise<any>;
  parseBeforeSubmit?: (values: Values) => Values;
  handleDraftDataBeforeSave?: (values: Values, initialValues?: Values) => Values;
  setInitialValues?: (initialValue: Values) => Values;
  getOptionalFields?: (values: Values) => Record<string, string>;
  validationValues?: Values;
  withAutoSaveDraft?: boolean;
  shouldSaveDraft?: (changedValues?: any, values?: any) => boolean;
  withDefaultOnFinish?: boolean;
  withDefaultValidation?: boolean;
  stepKey?: any;
}

function WizardForm<Values extends Record<string, any> = ApplicationPayload>(
  props: WizardFormProps<Values>
): JSX.Element {
  const {
    stage,
    payloadKey,
    validationFnName,
    onValuesChange,
    onBeforeFinish,
    onFinishFailed,
    parseBeforeSubmit,
    handleDraftDataBeforeSave = (values, initialValues) => {
      return produce<any>(initialValues, (draft) => {
        // TODO: find out purpose of this code. I totally don't remember :(
        mergeWith(draft, values, (draftValue, value) => {
          if (Array.isArray(value)) {
            return value;
          }
          return undefined;
        });
      });
    },
    getOptionalFields,
    form: formFromProps,
    disabled,
    setInitialValues,
    shouldSaveDraft = () => true,
    withAutoSaveDraft = true,
    withDefaultOnFinish = true,
    withDefaultValidation = true,
    onFinish,
    name,
    ...rest
  } = props;
  // location.pathname is also default 'formName' prop for submit button
  const location = useLocation();
  const actualFormName = name || location.pathname;

  const draftQuery = useGetDraft<Values>();
  const draftMutation = useSaveDraft();
  const [, handleCommonErrors] = useCommonErrors(actualFormName);
  const applicationQuery = useGetApplication();

  // Validation
  const [form] = Form.useForm<Values>(formFromProps);

  const getFormState = () => form.getFieldsValue();
  const validatorController = useValidator(
    getFormState,
    validationFnName,
    stage || applicationQuery?.data?.stage
  );

  // initialValues
  const isFormNotReady = (draftQuery.isLoading && !draftQuery.data) || applicationQuery.isLoading;
  const initialValues = useMemo(() => {
    if (!isFormNotReady && applicationQuery.data) {
      let rawPayload = draftQuery.data;
      if (!rawPayload) {
        const sourcePayload = applicationQuery.data?.payload?.[payloadKey];
        const providedPayload = applicationQuery.data?.payload?.provided?.[payloadKey];
        const isArray = Array.isArray(providedPayload) || Array.isArray(sourcePayload);
        if (isArray) {
          rawPayload = {
            [payloadKey]: merge(cloneDeep(providedPayload || []), sourcePayload || []),
          } as Values;
        } else {
          rawPayload = {
            [payloadKey]: merge(cloneDeep(providedPayload || {}), sourcePayload || {}),
          } as Values;
        }
      }
      const result = setInitialValues ? setInitialValues(rawPayload) : rawPayload;
      return result;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormNotReady]);

  //
  const optionalFieldsController = useOptionalFields({
    getOptionalFields,
    validationFnName,
    initialValues,
  });

  // Draft
  const { onFresh, onStale } = useDraftIsStale(payloadKey);
  const lastSavedDraft = useRef<Values>();

  const debouncedSaveChanges = useDebouncedCallback(async (formValues: any) => {
    const stepData = handleDraftDataBeforeSave(formValues, initialValues);
    const previous = lastSavedDraft.current || initialValues;
    if (!isEqual(stepData[payloadKey], previous?.[payloadKey])) {
      await draftMutation.mutateAsync({ stepData });
      lastSavedDraft.current = stepData;
    }
    onFresh();
  }, SAVE_DRAFT_DEBOUNCE_MS);

  const handleValuesChange = useMutableCallback(async (changedValues: any, values: Values) => {
    if (typeof onValuesChange === 'function') {
      onValuesChange(changedValues, values);
    }
    if (withAutoSaveDraft && shouldSaveDraft(changedValues, values)) {
      onStale();
      await debouncedSaveChanges(values);
    }
    optionalFieldsController.debouncedUpdate(values);
    handleCommonErrors.clearError();
  });

  // Submit
  const submitMutation = useSubmitStep();
  const agentUpdatedFieldsMutation = usePostAgentUpdatedFields();
  const onFinishInternal = async (values: Values) => {
    const result = parseBeforeSubmit ? parseBeforeSubmit(values) : values;
    if (onBeforeFinish) {
      await onBeforeFinish(result);
    }

    if (withDefaultOnFinish) {
      const fullPayload = { ...applicationQuery.data?.payload, ...values };
      const errors = validatorController.validateFn?.(fullPayload as any, getRoles());
      if (!isEmpty(errors)) {
        const errorFields = Object.entries(errors).map(([errName, errMessage]) => ({
          name: errName.split('.'),
          errors: [errMessage],
        }));

        onFinishFailed?.({ values: fullPayload, errorFields, outOfDate: false });
        handleCommonErrors.onError(errorFields);
        return Promise.reject(errors);
      }

      const oldApplicationPayload = applicationQuery.data?.payload;
      const applicationStage = applicationQuery.data?.stage;
      const applicationUid = applicationQuery.data?.uid;
      const activeStepKey = stagePayload?.activeStep?.key;
      const isTrackedAgentReviewStep =
        isAgent() &&
        (applicationStage === Stage.ksaReview || applicationStage === Stage.ksaContract) &&
        !!activeStepKey &&
        // Bank review saves/submissions are audited atomically by the server.
        activeStepKey !== 'review.bankAccount' &&
        TRACKED_STEP_KEYS.includes(activeStepKey);

      if (onFinish) {
        await onFinish(result);
      } else {
        await submitMutation.mutateAsync(result);
      }

      if (isTrackedAgentReviewStep && applicationUid) {
        try {
          const updatedFields = buildUpdatedFieldsDiff(
            oldApplicationPayload,
            result,
            payloadKey as string
          );
          if (updatedFields.length) {
            agentUpdatedFieldsMutation.mutate({
              applicationId: applicationUid,
              stepKey: activeStepKey,
              updatedFields,
            });
          }
        } catch (e) {
          console.error('Failed to send agent activity log update', e);
        }
      }
    }
    return Promise.resolve();
  };

  useEffect(() => {
    return () => {
      handleCommonErrors.clearError();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  if (isFormNotReady) return <Spin />;
  return (
    <FormItemContextProvider
      value={{
        defaultValidator: withDefaultValidation ? validatorController.validator : undefined,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: applicationQuery.data?.payload?.errorInfo?.errors,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabled:
          isReadOnly(applicationQuery.data) &&
          !(isAgent() && applicationQuery.data?.stage !== 'ksaApplication'),
        disabledByDefault:
          (disabled ||
            isReadOnly(applicationQuery.data) ||
            isSomeFieldsDisabled(applicationQuery.data)) &&
          !(isAgent() && applicationQuery.data?.stage !== 'ksaApplication'),
      }}
    >
      <FormWithMemo
        onValuesChange={handleValuesChange}
        initialValues={initialValues}
        name={actualFormName}
        form={form}
        onFinish={onFinishInternal}
        onFinishFailed={onFinishFailed}
        {...rest}
      />
    </FormItemContextProvider>
  );
}

const FormWithMemo = React.memo(Form);

const { Item, List, useForm, useWatch, Provider, Visible, ShouldUpdate } = Form;

WizardForm.Item = Item;
WizardForm.List = List;
WizardForm.useForm = useForm;
WizardForm.useWatch = useWatch;
WizardForm.Provider = Provider;
WizardForm.Visible = Visible;
WizardForm.ShouldUpdate = ShouldUpdate;

export default WizardForm;
